// Bar charts by the brand book (Data visualization): one y-axis in hours, 2 px gaps, 4 px rounded data ends,
// labels in ink / ink-2 (never in the series color), a hover tooltip on glass, and always a table view next to it.
import { useEffect, useRef, useState } from 'react';
import { cx } from './ds.tsx';
import { useFmt } from '../store/hooks.tsx';

export interface Bar {
  key: string;
  label: string;
  value: number;
  color: string;
}

export interface BarGroup {
  key: string;
  label: string;
  bars: Bar[];
}

const HOUR = 3_600_000;

function niceStep(max: number): number {
  const rough = max / 4;
  for (const step of [0.25, 0.5, 1, 2, 4, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000]) if (step >= rough) return step;
  return Math.ceil(rough / 1000) * 1000;
}

/** Stacked bars (groups that partition the time) or side by side (groups that overlap, like sources and providers). */
export function BarChart({ groups, height = 260, stacked = true, format, label, unit = 'h' }: {
  groups: BarGroup[]; height?: number; stacked?: boolean; format: (value: number) => string; label: string; unit?: string;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const fmt = useFmt();
  const plotRef = useRef<HTMLDivElement>(null);
  const [plotWidth, setPlotWidth] = useState(0);
  useEffect(() => {
    const el = plotRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => setPlotWidth(entry?.contentRect.width ?? 0));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const totals = groups.map((g) => (stacked ? g.bars.reduce((n, b) => n + b.value, 0) : Math.max(0, ...g.bars.map((b) => b.value))));
  const maxHours = Math.max(1, ...totals.map((v) => v / HOUR));
  const step = niceStep(maxHours);
  const top = Math.ceil(maxHours / step) * step;
  const ticks = Array.from({ length: Math.round(top / step) + 1 }, (_, i) => i * step);
  const plot = height - 24;
  const px = (ms: number) => (ms / HOUR / top) * plot;
  const hovered = groups.find((g) => g.key === hover);
  // Many days in a narrow chart: every n-th label, each one whole, instead of all of them cut to "01.0…".
  const longest = Math.max(0, ...groups.map((g) => g.label.length));
  const slot = groups.length > 0 ? plotWidth / groups.length : 0;
  const every = slot > 0 ? Math.max(1, Math.ceil((longest * 6.5 + 8) / slot)) : 1;
  return (
    <div className="chart" role="img" aria-label={label} style={{ height }} onMouseLeave={() => setHover(null)}>
      <div className="chart__axis" style={{ height: plot }}>
        {ticks.map((v) => (
          <span key={v} className="chart__tick" style={{ bottom: (v / top) * plot }}>{unit === 'h' ? fmt.chartDuration(v) : `${fmt.number(v, Number.isInteger(v) ? 0 : 2)} ${unit}`}</span>
        ))}
      </div>
      <div className="chart__plot" style={{ height: plot }} ref={plotRef}>
        {ticks.map((v) => <span key={v} className="chart__grid" style={{ bottom: (v / top) * plot }} />)}
        <div className="chart__groups">
          {groups.map((g, index) => (
            <div key={g.key} className={cx('chart__group', hover !== null && hover !== g.key && 'is-dim')} onMouseEnter={() => setHover(g.key)}
              onFocus={() => setHover(g.key)} tabIndex={0} aria-label={`${g.label}: ${g.bars.map((b) => `${b.label} ${format(b.value)}`).join(', ')}`}>
              <div className={stacked ? 'chart__stack' : 'chart__side'}>
                {(stacked ? [...g.bars].reverse() : g.bars).filter((b) => b.value > 0).map((b) => (
                  <span key={b.key} className="chart__bar" style={{ height: Math.max(2, px(b.value)), background: b.color }} />
                ))}
              </div>
              {index % every === 0 ? <span className="chart__label">{g.label}</span> : null}
            </div>
          ))}
        </div>
        {hovered ? (
          <div className="chart__tip tl-tip" role="tooltip">
            <b>{hovered.label}</b>
            {[...hovered.bars].filter((b) => b.value > 0).sort((a, b) => b.value - a.value).slice(0, 6).map((b) => (
              <span key={b.key} className="chart__tipline"><span className="tl-legend__sw" style={{ background: b.color }} />{b.label}<em>{format(b.value)}</em></span>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function Legend({ items }: { items: { key: string; label: string; color: string }[] }) {
  return (
    <div className="tl-legend">
      {items.map((i) => <span key={i.key} className="tl-legend__item"><span className="tl-legend__sw" style={{ background: i.color }} />{i.label}</span>)}
    </div>
  );
}
